// packages
import React, { useCallback, useState } from 'react';

// styles
import styles from './Attachment.module.scss';

// markup
import { Spinner } from '../Spinner';
import { Icon } from '../../blocks/icons';

// utilities
import { useToast } from '../../hooks/useToast';
import { getIconTypeForDocument } from '../../lib/helpers';

/**
 * Wrapper to fetch invoice attachment on every click so the signedUrl timeout isn't a factor and we maintain security
 */

export type AttachmentProps = {
	getUrlMethod: () => Promise<{ url?: string | null; errors?: any[] }>;
	children: React.JSX.Element;
	fileType?: string;
};

const Attachment: React.FC<AttachmentProps> = ( {
	getUrlMethod,
	children,
	fileType,
} ) => {
	const { showError } = useToast();
	const [ isLoading, setIsLoading ] = useState( false );
	const fetchUrl = useCallback( async () => {
		const { url, errors } = await getUrlMethod();
		// If they're using the API, then these errors
		if ( Array.isArray( errors ) ) {
			errors.forEach( ( error ) => showError( error ) );
			// otherwise, handle other datatypes, like objects (maybe Error objects) and strings
		} else if ( errors ) {
			showError( errors );
			// and if we have a URL, use it!
		} else if ( url ) {
			// open the attachment URL which should start it downloading without actually changing the page
			window.open( url, '_blank' );
		} else {
			// we should not get here, but if we do...
			showError( 'No attachment download URL found.' );
		}
		setIsLoading( false );
	}, [
		getUrlMethod,
		setIsLoading,
		showError
	] );
	const iconType = getIconTypeForDocument( { fileType }, true );
	return (
		<button
			className={ styles.attachment }
			data-cy='attachment-link'
			onClick={ () => {
				// run inside state callback so we have the right version of state
				setIsLoading( ( prevState ) => {
					if ( !prevState ) {
						fetchUrl();
						return true;
					} else {
						return prevState;
					}
				} );
			} }
		>
			{ isLoading ? (
				<Spinner className={ styles.spinner } />
			) : (
				<Icon type={ iconType } className={ styles.icon } />
			) }
			{ children }
		</button>
	);
};

export default Attachment;
